// Bridges real Supabase reviews into the existing Case-shaped UI.
// Keeps demo fallback intact so unauthenticated / empty-state views still render.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { documentedMarketRecovery, resolveCanonicalRecovery } from "@/lib/report/canonical-recovery";

import type { Case, CaseAISignals, CaseFile, CasePrimaryInvoice, SubmissionMethod } from "@/lib/cases";
import type {
  Review,
  ReviewFile,
  TimelineEvent,
  InternalNote,
  InvoiceData,
  AIFindings,
  FindingItem,
  LegalResearch,
  MarketComparison,
  MarketResearch,
  MarketResearchSource,

  Report,
  ReviewJob,
} from "@/lib/db/models";
import { certifyStorageComparisons } from "@/lib/report/certify-storage-comparisons";
import { dedupeLegalAuthorities } from "@/lib/legal/authority-set";
import {
  filterAuthoritiesByJurisdiction,
  jurisdictionScope,
  type JurisdictionScope,
} from "@/lib/legal/authority-jurisdiction";
import { stateFromZip } from "@/lib/legal/jurisdiction";
import { EMPTY_TIMESTAMPS, type ClaimTimestamps } from "@/lib/claim-timestamps";
import type { CaseStatus } from "@/lib/cases";

function methodOf(m: string | null | undefined): SubmissionMethod {
  return m === "photo" ? "photo" : m === "email" ? "email" : "upload";
}

export function mapDbStatusToCase(s: string): CaseStatus {
  switch (s) {
    /* No AI work starts until Stripe confirms payment, so a review still at
       the initial persisted status is presented as "Awaiting Payment". */
    case "submitted":
    case "received":
      return "awaiting_payment";
    case "ai_review_pending":
    case "ai_review_in_progress":
    case "reviewing":
    // Findings persisted but report not yet generated — still processing to
    // the customer's eye. Do NOT collapse into report_ready; that would light
    // up "Open Report" before the report row exists.
    case "findings_ready":
      return "reviewing";
    case "report_ready":
      return "report_ready";
    case "self_serve":
      return "self_serve";
    case "expert_negotiation_requested":
    case "negotiating":
      return "negotiating";
    case "waiting_on_vendor":
    case "waiting_vendor":
      return "waiting_vendor";
    case "waiting_on_customer":
    case "offer_received":
      return "offer_received";
    case "cancelled":
    case "completed":
      return "completed";
    case "action_required":
      return "action_required";
    case "awaiting_customer":
      return "awaiting_customer";
    case "pipeline_blocked":
      return "pipeline_blocked";
    default:
      return "awaiting_payment";
  }
}

/** Extract [low, high] from ai_findings.estimated_fair_range JSON if valid. */
function extractFairRange(raw: unknown): [number, number] | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const low = typeof r.low === "number" ? r.low : Number(r.low);
  const high = typeof r.high === "number" ? r.high : Number(r.high);
  if (!Number.isFinite(low) || !Number.isFinite(high)) return null;
  return [Math.max(0, low), Math.max(0, high)];
}

/** Best-effort number parse for values persisted as text in invoice_data.extracted. */
function coerceNumber(raw: unknown): number | undefined {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const n = Number(raw.replace(/[^0-9.\-]/g, ""));
    if (Number.isFinite(n) && n > 0) return n;
  }
  return undefined;
}

function firstString(raw: Record<string, unknown> | undefined, keys: string[]): string | undefined {
  if (!raw) return undefined;
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

/**
 * Fill a Review row's blank customer-facing fields (vendor / total / date /
 * zip / invoice_type) from the latest invoice_data.extracted JSON. Never
 * overwrites values the customer or the persisted `reviews` row already has —
 * this is a display-time fallback only.
 */
function mergeInvoiceIntoReview(review: Review, extracted: Record<string, unknown> | undefined): Review {
  if (!extracted) return review;
  const location = (extracted.location as Record<string, unknown> | undefined) ?? undefined;
  const vendor = review.vendor?.trim()
    ? review.vendor
    : firstString(extracted, ["vendor_name", "vendor"]) ?? review.vendor;
  const total =
    review.total_amount != null
      ? review.total_amount
      : coerceNumber(extracted.total_amount) ??
        coerceNumber(extracted.total_due) ??
        coerceNumber(extracted.amount_due) ??
        coerceNumber(extracted.balance_due) ??
        null;
  const invoiceDate = review.invoice_date
    ? review.invoice_date
    : firstString(extracted, ["invoice_date", "invoiceDate"]) ?? review.invoice_date;
  const zip = review.zip?.trim()
    ? review.zip
    : firstString(location, ["zip5", "zip", "postal_code"]) ??
      firstString(extracted, ["zip_code", "zip"]) ??
      review.zip;
  const invoiceType = review.invoice_type?.trim()
    ? review.invoice_type
    : firstString(extracted, ["invoice_type", "invoiceType"]) ?? review.invoice_type;
  return {
    ...review,
    vendor: vendor ?? null,
    total_amount: total,
    invoice_date: invoiceDate ?? null,
    zip: zip ?? null,
    invoice_type: invoiceType ?? null,
  };
}

export function reviewToCase(
  r: Review,
  files: CaseFile[] = [],
  ai?: CaseAISignals,
  primaryInvoice?: CasePrimaryInvoice | null,
  timestamps?: ClaimTimestamps,
): Case {
  return {
    id: r.file_number,
    createdAt: r.created_at,
    method: methodOf(r.submission_method),
    fastTrack: r.fast_track,
    details: {
      vendor: r.vendor ?? undefined,
      invoiceDate: r.invoice_date ?? undefined,
      invoiceType: r.invoice_type ?? undefined,
      totalAmount:
        r.total_amount != null ? `$${Number(r.total_amount).toLocaleString()}` : undefined,
      urgency: (r.urgency as Case["details"]["urgency"]) ?? undefined,
      zip: r.zip ?? undefined,
      notes: r.notes ?? undefined,
      // Customer-provided context (structured columns; never OCR facts).
      insuranceCompany: r.insurance_company ?? undefined,
      claimNumber: r.claim_number ?? undefined,
      adjusterName: r.adjuster_name ?? undefined,
      adjusterEmail: r.adjuster_email ?? undefined,
      vehicleDescription: r.vehicle_description ?? undefined,
      vehicleVin: (r as { vehicle_vin?: string | null }).vehicle_vin ?? undefined,
      vendorPhone: (r as { vendor_phone?: string | null }).vendor_phone ?? undefined,
      storageReason: r.storage_reason ?? undefined,
      reviewFocus: r.review_focus ?? undefined,
      additionalInfo: r.customer_context ?? undefined,

    },
    files,
    status: mapDbStatusToCase(r.status),
    negotiationChosen: r.negotiation_chosen,
    ai,
    primaryInvoice: primaryInvoice ?? null,
    timestamps: timestamps ?? {
      ...EMPTY_TIMESTAMPS,
      submittedAt: r.created_at,
      lastUpdatedAt: r.updated_at ?? r.created_at,
    },
  };
}

function reviewFilesToCaseFiles(files: ReviewFile[]): CaseFile[] {
  return files.map((f) => ({
    name: f.file_name,
    size: f.file_size ?? 0,
    type: f.mime_type ?? "",
  }));
}

const DEFAULT_PAGE_SIZE = 25;

export interface UseMyRealReviewsOptions {
  pageSize?: number;
}

export interface UseMyRealReviewsResult {
  reviews: Review[];
  loading: boolean;
  loadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  /** Server-side total number of reviews for this customer. */
  total: number;
  loadMore: () => void;
}

export function useMyRealReviews(
  options: UseMyRealReviewsOptions = {},
): UseMyRealReviewsResult {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const { user, loading: authLoading } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  // Reset when the signed-in user changes.
  useEffect(() => {
    setReviews([]);
    setPage(0);
    setHasMore(false);
  }, [user?.id]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setReviews([]);
      setLoading(false);
      setHasMore(false);
      setTotal(0);
      return;
    }
    let cancelled = false;
    const isFirstPage = page === 0;
    if (isFirstPage) setLoading(true);
    else setLoadingMore(true);
    const from = page * pageSize;
    const to = from + pageSize - 1;
    supabase
      .from("reviews")
      .select("*", { count: "exact" })
      .eq("customer_id", user.id)
      .order("created_at", { ascending: false })
      .range(from, to)
      .then(({ data, error, count }) => {
        if (cancelled) return;
        if (error) setError(error as unknown as Error);
        const rows = (data ?? []) as Review[];
        setReviews((prev) => (isFirstPage ? rows : [...prev, ...rows]));
        const totalCount = count ?? 0;
        setTotal(totalCount);
        setHasMore(from + rows.length < totalCount);
        setLoading(false);
        setLoadingMore(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading, page, pageSize]);

  return {
    reviews,
    loading,
    loadingMore,
    error,
    hasMore,
    total,
    loadMore: () => setPage((p) => p + 1),
  };
}

/**
 * Enriches a set of `reviews` rows into UI Case objects using the canonical
 * findings / reports / invoice / market sources. Shared by the paginated
 * dashboard table and the account-wide summary tiles so both read identical
 * canonical recovery values.
 */
function useEnrichedCases(reviews: Review[]): Case[] {
  const reviewIds = useMemo(() => reviews.map((r) => r.id), [reviews]);
  const idsKey = reviewIds.slice().sort().join(",");


  const [findingsMap, setFindingsMap] = useState<Record<string, AIFindings>>({});
  const [reportsMap, setReportsMap] = useState<Record<string, Report>>({});
  const [invoiceMap, setInvoiceMap] = useState<Record<string, InvoiceData>>({});
  const [primaryInvoiceMap, setPrimaryInvoiceMap] = useState<Record<string, CasePrimaryInvoice>>({});
  // Documented above-market evidence per review — the canonical recovery floor.
  const [marketRecoveryMap, setMarketRecoveryMap] = useState<Record<string, number>>({});
  const [comparisonsMap, setComparisonsMap] = useState<Record<string, Record<string, unknown>[]>>({});
  const [researchMap, setResearchMap] = useState<Record<string, Record<string, unknown>[]>>({});
  // Lifecycle timestamps (payment received / processing started) per review.
  const [timestampMap, setTimestampMap] = useState<Record<string, Partial<ClaimTimestamps>>>({});


  useEffect(() => {
    if (!reviewIds.length) {
      setFindingsMap({});
      setReportsMap({});
      setInvoiceMap({});
      setPrimaryInvoiceMap({});
      setMarketRecoveryMap({});
      setComparisonsMap({});
      setResearchMap({});
      setTimestampMap({});
      return;
    }

    let cancelled = false;
    (async () => {
      const [afRes, repRes, invRes, filesRes, mrRes, mcRes, jobsRes, billRes] = await Promise.all([
        supabase
          .from("ai_findings")
          .select("*")
          .in("review_id", reviewIds)
          .order("updated_at", { ascending: false }),
        supabase
          .from("reports")
          .select("*")
          .in("review_id", reviewIds)
          .eq("status", "ready")
          .order("version", { ascending: false }),
        supabase
          .from("invoice_data")
          .select("*")
          .in("review_id", reviewIds)
          .order("updated_at", { ascending: false }),
        supabase
          .from("review_files")
          .select("id,review_id,bucket,storage_path,file_name,mime_type,file_size,kind,created_at")
          .in("review_id", reviewIds)
          .order("created_at", { ascending: true }),
        supabase
          .from("market_research")
          .select("*")
          .in("review_id", reviewIds),
        supabase
          .from("market_comparisons")
          .select("*")
          .in("review_id", reviewIds),
        supabase
          .from("review_jobs")
          .select("review_id,started_at,created_at")
          .in("review_id", reviewIds)
          .order("created_at", { ascending: true }),
        supabase
          .from("billing_records")
          .select("review_id,status,created_at,updated_at")
          .in("review_id", reviewIds)
          .order("created_at", { ascending: true }),
      ]);
      if (cancelled) return;
      const mrByReview: Record<string, { status: string | null; difference_amount: number | null }[]> = {};
      for (const row of (mrRes.data ?? []) as {
        review_id: string;
        status: string | null;
        difference_amount: number | null;
      }[]) {
        (mrByReview[row.review_id] ||= []).push({
          status: row.status,
          difference_amount: row.difference_amount,
        });
      }
      const researchByReview: Record<string, Record<string, unknown>[]> = {};
      for (const row of (mrRes.data ?? []) as Record<string, unknown>[]) {
        (researchByReview[row.review_id as string] ||= []).push(row);
      }
      const comparisonsByReview: Record<string, Record<string, unknown>[]> = {};
      for (const row of (mcRes.data ?? []) as Record<string, unknown>[]) {
        (comparisonsByReview[row.review_id as string] ||= []).push(row);
      }
      setResearchMap(researchByReview);
      setComparisonsMap(comparisonsByReview);
      const mrRecovery: Record<string, number> = {};
      for (const [rid, rows] of Object.entries(mrByReview)) {
        mrRecovery[rid] = documentedMarketRecovery(rows);
      }

      const af: Record<string, AIFindings> = {};
      for (const row of (afRes.data ?? []) as AIFindings[]) {
        if (!af[row.review_id]) af[row.review_id] = row;
      }
      const rep: Record<string, Report> = {};
      for (const row of (repRes.data ?? []) as Report[]) {
        if (!rep[row.review_id]) rep[row.review_id] = row;
      }
      const inv: Record<string, InvoiceData> = {};
      for (const row of (invRes.data ?? []) as InvoiceData[]) {
        if (!inv[row.review_id]) inv[row.review_id] = row;
      }
      // Pick the "primary" invoice per review: kind='invoice' first, else the
      // earliest uploaded file. Track total file count for the chooser hint.
      const byReview: Record<string, ReviewFile[]> = {};
      for (const row of (filesRes.data ?? []) as ReviewFile[]) {
        (byReview[row.review_id] ||= []).push(row);
      }
      const primary: Record<string, CasePrimaryInvoice> = {};
      for (const [rid, list] of Object.entries(byReview)) {
        if (!list.length) continue;
        const invoice =
          list.find((f) => (f.kind ?? "").toLowerCase() === "invoice") ?? list[0];
        primary[rid] = {
          id: invoice.id,
          bucket: invoice.bucket,
          storage_path: invoice.storage_path,
          file_name: invoice.file_name,
          mime_type: invoice.mime_type,
          file_size: invoice.file_size,
          created_at: invoice.created_at,
          total_files: list.length,
        };
      }
      const stamps: Record<string, Partial<ClaimTimestamps>> = {};
      for (const row of (jobsRes.data ?? []) as {
        review_id: string;
        started_at: string | null;
        created_at: string;
      }[]) {
        const at = row.started_at ?? row.created_at;
        const cur = stamps[row.review_id] ?? (stamps[row.review_id] = {});
        if (!cur.processingStartedAt || at < cur.processingStartedAt) cur.processingStartedAt = at;
      }
      for (const row of (billRes.data ?? []) as {
        review_id: string | null;
        status: string | null;
        created_at: string;
        updated_at: string | null;
      }[]) {
        if (!row.review_id) continue;
        const paid = ["paid", "succeeded", "captured", "complete", "completed"].includes(
          (row.status ?? "").toLowerCase(),
        );
        if (!paid) continue;
        const at = row.updated_at ?? row.created_at;
        const cur = stamps[row.review_id] ?? (stamps[row.review_id] = {});
        if (!cur.paymentReceivedAt || at < cur.paymentReceivedAt) cur.paymentReceivedAt = at;
      }
      setTimestampMap(stamps);
      setFindingsMap(af);
      setReportsMap(rep);
      setInvoiceMap(inv);
      setPrimaryInvoiceMap(primary);
      setMarketRecoveryMap(mrRecovery);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const cases = useMemo(
    () =>
      reviews.map((r) => {
        const finding = findingsMap[r.id];
        const report = reportsMap[r.id];
        const invoice = invoiceMap[r.id];
        // Canonical recovery: ai_findings.estimated_savings, with documented
        // above-market evidence as the floor (see canonical-recovery.ts).
        const rawSavings =
          finding && typeof finding.estimated_savings === "number"
            ? Number(finding.estimated_savings)
            : finding && typeof finding.potential_savings === "number"
              ? Number(finding.potential_savings)
              : null;
        const invoiceTotal =
          typeof r.total_amount === "number" ? r.total_amount : Number(r.total_amount);
        const resolved = resolveCanonicalRecovery({
          findingsSavings: rawSavings,
          // Certified recovery scope — identical inputs to the opened report,
          // so the dashboard figure can never diverge from Chapter 02.
          chargeInputs: {
            lineItems: (invoice?.line_items ?? []) as Array<Record<string, unknown>>,
            marketComparisons: (comparisonsMap[r.id] ?? []) as never,
            marketResearch: (researchMap[r.id] ?? []) as never,
          },
          documentedRecovery: marketRecoveryMap[r.id] ?? 0,
          invoiceTotal: Number.isFinite(invoiceTotal) ? invoiceTotal : null,
        });
        const ai: CaseAISignals = {
          potentialSavings:
            resolved.basis === "none" ? null : resolved.amount,
          fairRange:
            resolved.fairPaymentLow !== null && resolved.fairPaymentHigh !== null
              ? ([resolved.fairPaymentLow, resolved.fairPaymentHigh] as [number, number])
              : extractFairRange(finding?.estimated_fair_range ?? null),
          confidence:
            finding && typeof finding.confidence === "number"
              ? Number(finding.confidence)
              : null,
          negotiationStrength: finding?.negotiation_strength ?? null,
          reportReady: !!report,
          reportId: report?.id ?? null,
          hasFindings: !!finding,
        };
        const merged = mergeInvoiceIntoReview(r, invoice?.extracted);
        const stamps = timestampMap[r.id] ?? {};
        return reviewToCase(merged, [], ai, primaryInvoiceMap[r.id] ?? null, {
          ...EMPTY_TIMESTAMPS,
          submittedAt: r.created_at,
          paymentReceivedAt: stamps.paymentReceivedAt ?? null,
          processingStartedAt: stamps.processingStartedAt ?? null,
          reportCompletedAt: report?.generated_at ?? report?.updated_at ?? null,
          lastUpdatedAt: r.updated_at ?? r.created_at,
        });
      }),
    [
      reviews,
      findingsMap,
      reportsMap,
      invoiceMap,
      primaryInvoiceMap,
      timestampMap,
      marketRecoveryMap,
      comparisonsMap,
      researchMap,
    ],
  );

  return cases;
}

/**
 * Returns real reviews mapped into the UI's Case shape, enriched with the
 * latest ai_findings / reports / invoice_data per review. This is what
 * powers the dashboard report table / vendor / total columns — never the
 * demo heuristic in buildReport().
 */
export function useMyReviewsAsCases(options: UseMyRealReviewsOptions = {}) {
  const { reviews, loading, loadingMore, error, hasMore, total, loadMore } =
    useMyRealReviews(options);
  const cases = useEnrichedCases(reviews);

  return {
    cases,
    loading,
    loadingMore,
    error,
    hasReal: cases.length > 0,
    hasMore,
    total,
    loadMore,
  };
}

/**
 * ACCOUNT-WIDE summary source. The dashboard summary tiles must describe the
 * customer's ENTIRE account, never the currently loaded page, so this hook
 * loads every authorized review row (RLS-scoped) and enriches them through
 * the same canonical path as the paginated table. Pagination, sorting,
 * filtering and table rendering are untouched.
 */
export function useMyAccountCases() {
  const { user, loading: authLoading } = useAuth();
  const [allReviews, setAllReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setAllReviews([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const CHUNK = 1000;
      const collected: Review[] = [];
      for (let page = 0; page < 20; page++) {
        const { data, error } = await supabase
          .from("reviews")
          .select("*")
          .eq("customer_id", user.id)
          .order("created_at", { ascending: false })
          .range(page * CHUNK, page * CHUNK + CHUNK - 1);
        if (error) break;
        const rows = (data ?? []) as Review[];
        collected.push(...rows);
        if (rows.length < CHUNK) break;
      }
      if (cancelled) return;
      setAllReviews(collected);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading]);

  const cases = useEnrichedCases(allReviews);
  return { cases, loading };
}



export interface RealReviewBundle {
  review: Review | null;
  files: ReviewFile[];
  timeline: TimelineEvent[];
  loading: boolean;
  error: Error | null;
}

export function useRealReviewByFileNumber(fileNumber: string | undefined): RealReviewBundle {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<RealReviewBundle>({
    review: null,
    files: [],
    timeline: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user || !fileNumber) {
      setState({ review: null, files: [], timeline: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const { data: r, error } = await supabase
          .from("reviews")
          .select("*")
          .eq("file_number", fileNumber)
          .maybeSingle();
        if (error) throw error;
        if (!r) {
          if (!cancelled)
            setState({ review: null, files: [], timeline: [], loading: false, error: null });
          return;
        }
        const review = r as Review;
        const [filesRes, timelineRes] = await Promise.all([
          supabase
            .from("review_files")
            .select("*")
            .eq("review_id", review.id)
            .order("created_at", { ascending: false }),
          supabase
            .from("timeline_events")
            .select("*")
            .eq("review_id", review.id)
            .order("created_at", { ascending: true }),
        ]);
        if (cancelled) return;
        setState({
          review,
          files: (filesRes.data ?? []) as ReviewFile[],
          timeline: (timelineRes.data ?? []) as TimelineEvent[],
          loading: false,
          error: null,
        });
      } catch (e) {
        if (!cancelled)
          setState({
            review: null,
            files: [],
            timeline: [],
            loading: false,
            error: e as Error,
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, fileNumber, authLoading]);

  return state;
}

export function useRealReviewAsCase(fileNumber: string | undefined) {
  const bundle = useRealReviewByFileNumber(fileNumber);
  const caseObj = useMemo(
    () =>
      bundle.review ? reviewToCase(bundle.review, reviewFilesToCaseFiles(bundle.files)) : null,
    [bundle.review, bundle.files],
  );
  return { ...bundle, case: caseObj };
}

/* ─────────────────────── Staff: all reviews ─────────────────────── */

export function useAllRealReviews() {
  const { user, loading: authLoading } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setReviews([]);
      setTimeline([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [rRes, tRes] = await Promise.all([
          supabase.from("reviews").select("*").order("created_at", { ascending: false }).limit(50),
          supabase
            .from("timeline_events")
            .select("*")
            .order("created_at", { ascending: false })
            .limit(20),
        ]);
        if (cancelled) return;
        if (rRes.error) throw rRes.error;
        setReviews((rRes.data ?? []) as Review[]);
        setTimeline((tRes.data ?? []) as TimelineEvent[]);
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e as Error);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading]);

  return { reviews, timeline, loading, error, hasReal: reviews.length > 0 };
}

/* ─────────────────────── Full workspace bundle ─────────────────────── */

export interface FullWorkspaceBundle {
  review: Review | null;
  files: ReviewFile[];
  timeline: TimelineEvent[];
  notes: InternalNote[];
  invoiceData: InvoiceData | null;
  findings: AIFindings | null;
  findingItems: FindingItem[];
  legalResearch: LegalResearch[];
  /**
   * SAB-002a — the jurisdiction `legalResearch` was filtered against.
   * `resolved === false` means the review's state is unknown: consumers must
   * render the explicit pending/unavailable state, never a broad list.
   */
  legalJurisdiction: JurisdictionScope;
  marketComparisons: MarketComparison[];
  marketResearch: MarketResearch[];
  marketResearchSources: MarketResearchSource[];

  report: Report | null;
  jobs: ReviewJob[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

const EMPTY_BUNDLE: Omit<FullWorkspaceBundle, "refresh"> = {
  review: null,
  files: [],
  timeline: [],
  notes: [],
  invoiceData: null,
  findings: null,
  findingItems: [],
  legalResearch: [],
  legalJurisdiction: { state: null, locality: null, resolved: false },
  marketComparisons: [],
  marketResearch: [],
  marketResearchSources: [],

  report: null,
  jobs: [],
  loading: false,
  error: null,
};

export function useFullWorkspaceBundle(fileNumber: string | undefined): FullWorkspaceBundle {
  const { user, loading: authLoading } = useAuth();
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<Omit<FullWorkspaceBundle, "refresh">>({
    ...EMPTY_BUNDLE,
    loading: true,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user || !fileNumber) {
      setState({ ...EMPTY_BUNDLE });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        // Accept either a review UUID (from /report/$id, /workspace/$id) or a
        // human-facing file_number (INV-YYYY-XXXXXX). Detect UUIDs and query the
        // matching column; without this, UUID callers fell through to an empty
        // bundle and every downstream renderer (Chapter 03 market/legal, etc.)
        // rendered its empty state despite populated rows in the database.
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          fileNumber,
        );
        const reviewQuery = supabase.from("reviews").select("*");
        const { data: r, error } = await (isUuid
          ? reviewQuery.eq("id", fileNumber).maybeSingle()
          : reviewQuery.eq("file_number", fileNumber).maybeSingle());
        if (error) throw error;
        if (!r) {
          if (!cancelled) setState({ ...EMPTY_BUNDLE });
          return;
        }

        const review = r as Review;
        const rid = review.id;
        const [files, tl, notes, invD, ai, fi, lr, mc, mr, mrs, report, jobs] = await Promise.all([
          supabase
            .from("review_files")
            .select("*")
            .eq("review_id", rid)
            .order("created_at", { ascending: false }),
          supabase
            .from("timeline_events")
            .select("*")
            .eq("review_id", rid)
            .order("created_at", { ascending: true }),
          supabase
            .from("internal_notes")
            .select("*")
            .eq("review_id", rid)
            .order("created_at", { ascending: false }),
          supabase
            .from("invoice_data")
            .select("*")
            .eq("review_id", rid)
            .order("updated_at", { ascending: false })
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from("ai_findings")
            .select("*")
            .eq("review_id", rid)
            .order("updated_at", { ascending: false })
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase.from("finding_items").select("*").eq("review_id", rid).order("created_at", { ascending: true }),
          supabase.from("legal_research").select("*").eq("review_id", rid).order("created_at", { ascending: true }),
          supabase.from("market_comparisons").select("*").eq("review_id", rid).order("created_at", { ascending: true }),
          supabase.from("market_research").select("*").eq("review_id", rid).order("created_at", { ascending: true }),
          supabase
            .from("market_research_sources")
            .select("*, market_research!inner(review_id)")
            .eq("market_research.review_id", rid),

          supabase
            .from("reports")
            .select("*")
            .eq("review_id", rid)
            .eq("status", "ready")
            .order("version", { ascending: false })
            .order("generated_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from("review_jobs")
            .select("id,review_id,job_type,provider,status,started_at,completed_at,created_at")
            .eq("review_id", rid)
            .order("created_at", { ascending: true }),
        ]);
        if (cancelled) return;
        const reviewLoc = review as unknown as {
          location_resolved_state?: string | null;
          location_resolved_city?: string | null;
          vendor_state?: string | null;
          vendor_city?: string | null;
          state?: string | null;
          city?: string | null;
          zip?: string | null;
        };
        const legalScope = jurisdictionScope(
          reviewLoc.location_resolved_state ??
            reviewLoc.vendor_state ??
            reviewLoc.state ??
            stateFromZip(reviewLoc.zip ?? null),
          reviewLoc.location_resolved_city ?? reviewLoc.vendor_city ?? reviewLoc.city ?? null,
        );
        setState({
          review,
          files: (files.data ?? []) as ReviewFile[],
          timeline: (tl.data ?? []) as TimelineEvent[],
          notes: (notes.data ?? []) as InternalNote[],
          invoiceData: (invD.data ?? null) as InvoiceData | null,
          findings: (ai.data ?? null) as AIFindings | null,
          findingItems: (fi.data ?? []) as FindingItem[],
          // SAB-002: collapse duplicates, then SAB-002a: keep only the
          // authorities applicable to the review's resolved jurisdiction.
          legalResearch: filterAuthoritiesByJurisdiction(
            dedupeLegalAuthorities((lr.data ?? []) as LegalResearch[]),
            legalScope,
          ).applicable,
          legalJurisdiction: legalScope,
          marketComparisons: certifyStorageComparisons(
            (mc.data ?? []) as MarketComparison[],
            (invD.data ?? null) as InvoiceData | null,
          ),
          marketResearch: (mr.data ?? []) as unknown as MarketResearch[],
          marketResearchSources: (mrs.data ?? []) as unknown as MarketResearchSource[],

          report: (report.data ?? null) as Report | null,
          jobs: (jobs.data ?? []) as ReviewJob[],
          loading: false,
          error: null,
        });
      } catch (e) {
        if (!cancelled) setState({ ...EMPTY_BUNDLE, loading: false, error: e as Error });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, fileNumber, authLoading, tick]);

  return { ...state, refresh: () => setTick((t) => t + 1) };
}

export function bundleToCase(bundle: FullWorkspaceBundle): Case | null {
  if (!bundle.review) return null;
  return reviewToCase(bundle.review, reviewFilesToCaseFiles(bundle.files));
}
